"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

type FormData = {
  name: string;
  email: string;
  message: string;
};

type FormErrors = {
  name?: string;
  email?: string;
  message?: string;
};

const fieldSurface = {
  default: {
    input: `w-full rounded-lg border px-4 py-3 focus:outline-none focus:ring-2
      bg-white/50 dark:bg-dark/50
      border-gray-light/20 dark:border-gray-dark/20
      focus:ring-dark dark:focus:ring-white`,
    errorBorder: "border-red-500",
    errorRing: "focus:ring-red-500",
    label: "mb-2 block text-sm text-text-light dark:text-text-dark",
  },
  onDark: {
    input: `w-full rounded-lg border border-white/15 bg-white/10 px-4 py-3 text-white
      placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-white/30`,
    errorBorder: "border-red-400",
    errorRing: "focus:ring-red-400",
    label: "mb-2 block text-sm text-zinc-300",
  },
} as const;

export function ContactInquiryForm({
  surface = "default",
  idPrefix = "",
  className = "",
  /** No radius on fields / submit — used with `/lp/growth` rectilinear closing band. */
  squareCorners = false,
}: {
  /** `onDark` — inputs for charcoal / teal LP closing bands. */
  surface?: "default" | "onDark";
  /** Prefix element ids (e.g. `growth-lp-`) so labels stay unique if multiple forms mount. */
  idPrefix?: string;
  className?: string;
  squareCorners?: boolean;
}) {
  const router = useRouter();
  const s = fieldSurface[surface];
  const pid = (base: string) => `${idPrefix}${base}`;

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    setTimeout(() => {
      router.push("/thank-you");
    }, 500);
  };

  const inputClass = (field: keyof FormErrors) => {
    const shape = squareCorners
      ? s.input.replaceAll("rounded-lg", "rounded-none")
      : s.input;
    return `${shape} ${errors[field] ? `${s.errorBorder} ${s.errorRing}` : ""}`;
  };

  return (
    <form
      id={pid("township-form")}
      method="post"
      className={`space-y-6 ${className}`}
      noValidate
      onSubmit={handleSubmit}
    >
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <label htmlFor={pid("firstname")} className={s.label}>
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="firstname"
            id={pid("firstname")}
            required
            className={inputClass("name")}
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
          />
          {errors.name ? (
            <p className="mt-1 text-sm text-red-500">{errors.name}</p>
          ) : null}
        </div>
        <div>
          <label htmlFor={pid("email")} className={s.label}>
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            id={pid("email")}
            required
            className={inputClass("email")}
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
          />
          {errors.email ? (
            <p className="mt-1 text-sm text-red-500">{errors.email}</p>
          ) : null}
        </div>
      </div>
      <div className="pb-2">
        <label htmlFor={pid("message")} className={s.label}>
          Message <span className="text-red-500">*</span>
        </label>
        <textarea
          name="message"
          id={pid("message")}
          required
          rows={5}
          className={`${inputClass("message")} resize-none`}
          value={formData.message}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, message: e.target.value }))
          }
        />
        {errors.message ? (
          <p className="mt-1 text-sm text-red-500">{errors.message}</p>
        ) : null}
      </div>
      <div className={isSubmitting ? "pointer-events-none opacity-50" : ""}>
        <input
          type="submit"
          value={isSubmitting ? "Sending…" : "Send message"}
          className={
            surface === "onDark"
              ? `w-full cursor-pointer ${squareCorners ? "rounded-none" : "rounded-full"} bg-white px-6 py-3 text-center text-[15px] font-medium text-gray-950 transition hover:bg-zinc-200`
              : `group relative inline-block w-full cursor-pointer overflow-hidden ${squareCorners ? "rounded-none" : "rounded-full"} border-2 border-current px-6 py-3 text-center transition-transform duration-200 hover:scale-105`
          }
        />
      </div>
    </form>
  );
}
